
import ActionsNotes from '@/components/_ActionsNotes/index.vue';
import { GET_RECIPIENT, UPDATE_ACTION_METADATA, GET_PERSON_BY_EXTERNAL_ID, GET_CONTACTS, GET_ACTION } from '@/gql';
import axios from 'axios';
import { Base64 } from 'js-base64';
import { EventBus } from '@/eventBus';
import Editor from '@tinymce/tinymce-vue';
import HtmlRenderer from '@/components/HtmlRenderer.vue';
import JsonEditor from '@/components/JsonEditor/index.vue';
import { mapGetters, mapState } from 'vuex';
import getUserInfo from '@/cognito/getUserInfo';
import Contacts from '@/components/_Contacts/index.vue';
import { objIsEmpty } from '@/utils';
import { actionForceCancel, actionForceOpen, actionUpdateChannelEndpoint, actionUpdateDunningNotice } from '@/api/action';
import { Risolto_Action, Risolto_Action_Source_Enum, Risolto_Action_Status_Enum, Risolto_Channel, Risolto_Channel_Endpoint, Risolto_Channel_Enum, Risolto_Contact, Risolto_Dunning_Notice_Constraint } from '@/generated/graphql';
import LoadingButton from '@/components/_Buttons/LoadingButton.vue';
import Utils from '@/utils/utils';
import gql from 'graphql-tag';
import { Action_Metadata } from '@/schema/action';
import { Email_Metadata, Mail_Metadata, Sms_Metadata } from '@/schema/channel';
import { Address } from '@/schema/address';
import Logging from '@/logging';
import MainContacts from '@/components/_MainContacts/index.vue';
import { Attachment, S3_Attachment } from '@/schema/attachment';
import Vue from 'vue';
import VueTelInput from 'vue-tel-input';
import 'vue-tel-input/dist/vue-tel-input.css';
import { COUNTRIES } from '@/constants';
import AttachmentHelper from '@/helpers/attachment';

Vue.use(VueTelInput);
import { attachmentDownload } from '@/api/attachment';
import { hasLicenseAndPermission } from '@/helpers/permission-helper';

import CustomFieldsList from '@/components/_CustomFields/CustomFieldsList.vue';
import { renderTemplate } from '@/api/templating';
import AttachmentUploader from '@/components/_Attachments/AttachmentUploader.vue';
import EmailForm from '@/components/_Messaging/EmailForm.vue';

const LANGUAGES = gql`
query viewsDialogsActionDetail__languages(
  $participant: uuid!
  $legalEntity: uuid!
  $channel: risolto_channel_enum!
  $templateId: String!
) {
  custom: risolto_communication_template(
    where: {
      template_id: { _eq: $templateId }
      channel: { _eq: $channel }
      participant: { _eq: $participant }
      legal_entity: { _eq: $legalEntity }
    }
  ) {
    template_id
    participant
    locale
  }
  default: risolto_communication_template(
    where: {
      template_id: { _eq: $templateId }
      channel: { _eq: $channel }
      participant: { _eq: $participant }
      legal_entity_by_reference: {default: { _eq: true } }
    }
  ) {
    template_id
    participant
    locale
  }
  generic: risolto_communication_template(
    where: {
      template_id: { _eq: $templateId }
      channel: { _eq: $channel }
      participant: { _is_null:true }
    }
  ) {
    template_id
    participant
    locale
  }
}
`;

const TEMPLATES = gql`
query templates(
    $participant: uuid!,
  	$channel: risolto_channel_enum!
) {
    risolto_communication_template(
      distinct_on: template_id
      where: {
        channel: { _eq: $channel }
        _and: {
          _or: [
            { participant: { _eq: $participant } }
            { participant: { _is_null: true } }
          ]
        }
    }) {
        template_id
    }
}
`;

const GET_USER_INTERFACE_DETAILS = gql`
query ViewsDialogsActionDetail_userInterfaceDetails($participant: uuid!) {
	userInterfaceDetails: risolto_participant(
		where: {
			reference: { _eq: $participant }
		}
	) {
		custom_field_definitions: user_interface_details(path: "action.custom_field_definitions")
		disallowed_channels: user_interface_details(path: "action.disallowed_channels")
		disallowed_message_codes: user_interface_details(path: "action.disallowed_message_codes")
	}
}
`;

const GET_RECIPIENT_DOCUMENTS = gql`
query ViewsDialogsActionDetail_recipientRecord ($participant: uuid!, $recipient: uuid!, $today: date!) {
  risolto_recipient (where: {
    reference: { _eq: $recipient },
    participant: { _eq: $participant }
  }) {
    reference
    name
	last_dunning_costs
	legal_entity
    legal_entity_by_reference {
      reference
    }
	payment_plans {
		invoice_group_by_reference {
			invoice_group_invoices {
				invoice
			}
			invoice_group_credit_notes {
				credit_note
			}
			invoice_group_debit_notes {
				debit_note
			}
			invoice_group_payments {
				payment
			}
		}
	}
    invoice_documents {
      reference
	  invoice_number
	  open_amount
	  due_date
	  file_references
	  invoice_dispute: disputes(
			where: {
				until: { _gte: $today }
				resolved_at: { _is_null: true }
			}
			order_by: {
				until: desc
			}
			limit: 1
			) {
			reference
			until
			dispute_code_by_reference {
				code
			}
		}
    }
    credit_notes {
      reference
	  credit_note_number
	  issue_date
	  open_amount
	  file_references
    }
    debit_notes {
      reference
	  debit_note_number
	  issue_date
	  open_amount
    }
    payment_documents {
      reference
	  payment_number
	  issue_date
	  open_amount
    }
  }
}
`;

const GET_DUNNING_NOTICE = gql`
query ViewsDialogsActionDetail_dunningNotice ($participant: uuid!, $action: uuid!) {
  risolto_action (where: {
    reference: { _eq: $action },
    participant: { _eq: $participant }
  }) {
    reference
	metadata
	custom_fields
    dunning_notice_by_reference {
		charges
		interest
		invoice_group_by_reference {
			invoice_group_credit_notes {
				credit_note
			}
			invoice_group_debit_notes {
				debit_note
			}
			invoice_group_invoices {
				invoice
			}
			invoice_group_payments {
				payment
			}
		}
	}
  }
}
`;

const GET_EMAIL_ADDRESS_FROM = gql`
query ViewsDialogsActionDetail_emailAddressFrom($participant: uuid!, $action: uuid!) {
	risolto_action(
		where: {
			participant: { _eq: $participant }
			reference: { _eq: $action }
		}
	) {
		legal_entity_by_reference {
			email_addresses1: processing_details(path: "outbound_channel.email_addresses")
			email_addresses2: processing_details(path: "outbound_channel.communication.email_addresses")
		}
		channel_from: metadata(path: "channels[0]._from")
	}
}
`;

export default {
	components: {
		Editor,
		MainContacts,
		JsonEditor,
		Contacts,
		ActionsNotes,
		LoadingButton,
		CustomFieldsList,
		HtmlRenderer,
		AttachmentUploader,
		EmailForm
	},
	apollo: {
		languages: {
			query: LANGUAGES,
			variables() {
				return {
					participant: this.selectedParticipantStore,
					legalEntity: this.action.legal_entity,
					templateId: this.action.metadata.channels[0].template.template_id,
					channel: this.action.channel
				};
			},
			skip() {
				return !this.action || !this.action.metadata?.channels?.[0]?.template;
			},
			update(data) {
				let languages;

				if (data.custom?.length > 0) {
					languages = data.custom;
				} else if (data.default?.length > 0) {
					languages = data.default;
				} else {
					languages = data.generic;
				}

				return languages.map(item => item.locale);
			}
		},
		templates: {
			query: TEMPLATES,
			variables() {
				return {
					participant: this.selectedParticipantStore,
					channel: this.action.channel
				};
			},
			skip() {
				return !this.action;
			},
			update(data) {
				return data.risolto_communication_template.map(x => x.template_id);
			}
		},
		userInterfaceDetails: {
			query: GET_USER_INTERFACE_DETAILS,
			variables() {
				return {
					participant: this.selectedParticipantStore
				};
			},
			update: data => data.userInterfaceDetails[0]
		},
		recipientRecord: {
			query: GET_RECIPIENT_DOCUMENTS,
			variables() {
				return {
					participant: this.selectedParticipantStore,
					recipient: this.action.recipient,
					today: new Date().toISOString()
				};
			},
			skip() {
				return !this.action;
			},
			update: data => data.risolto_recipient[0]
		},
		dunningNotice: {
			query: GET_DUNNING_NOTICE,
			variables() {
				return {
					participant: this.selectedParticipantStore,
					action: this.action.reference
				};
			},
			skip() {
				return !this.action || !this.recipientRecord;
			},
			update(data) {
				if (!this.recipientRecord) {
					return;
				}

				if (!data.risolto_action || data.risolto_action[0].dunning_notice_by_reference === null) {
					return;
				}

				// Check if all invoices are shown
				const openInvoices = this.recipientRecord.invoice_documents.filter((invoice: any) => invoice.open_amount > 0.00).length;
				const showCheckAllInvoices = openInvoices !== this.recipientRecord.invoice_documents.length;

				// Check if all credit notes are shown
				const openCreditNotes = this.recipientRecord.credit_notes.filter((creditNote: any) => creditNote.open_amount > 0.00).length;
				const showCheckAllCreditNotes = openCreditNotes !== this.recipientRecord.credit_notes.length;

				// Check if all debit notes are shown
				const openDebitNotes = this.recipientRecord.debit_notes.filter((debitNote: any) => debitNote.open_amount > 0.00).length;
				const showCheckAllDebitNotes = openDebitNotes !== this.recipientRecord.debit_notes.length;

				// Check if all payments are shown
				const openPayments = this.recipientRecord.payment_documents.filter((payment: any) => payment.open_amount > 0.00).length;
				const showCheckAllPayments = openPayments !== this.recipientRecord.payment_documents.length;

				this.dunningNoticeFormState = {
					...this.dunningNoticeFormState,
					showCheckAllInvoices,
					showCheckAllCreditNotes,
					showCheckAllDebitNotes,
					showCheckAllPayments
				};

				const selectedInvoices = data.risolto_action[0].dunning_notice_by_reference.invoice_group_by_reference.invoice_group_invoices.map(x => x.invoice);
				const selectedCreditNotes = data.risolto_action[0].dunning_notice_by_reference.invoice_group_by_reference.invoice_group_credit_notes.map(x => x.credit_note);
				const selectedDebitNotes = data.risolto_action[0].dunning_notice_by_reference.invoice_group_by_reference.invoice_group_debit_notes.map(x => x.debit_note);
				const selectedPayments = data.risolto_action[0].dunning_notice_by_reference.invoice_group_by_reference.invoice_group_payments.map(x => x.payment_document);

				/**
				 * RecipientRecord.invoice_documents is a list of all invoices of the recipient.
				 * each has a list of file_references, which are the attachments of the invoice.
				 *
				 * check is one of the file_references is in the risolto_action[0].metadata.attachments array.
				 */
				const attachedInvoices = this.recipientRecord.invoice_documents.filter((invoice: any) => {
					return invoice.file_references.some((fileReference: any) => {
						return data?.risolto_action?.[0]?.metadata?.attachments?.some((attachment: any) => {
							return JSON.stringify(attachment) === JSON.stringify(fileReference);
						});
					});
				}).map((invoice: any) => invoice.reference);
				const attachedCreditNotes = this.recipientRecord.credit_notes.filter((creditNote: any) => {
					return creditNote.file_references.some((fileReference: any) => {
						return data?.risolto_action?.[0]?.metadata?.attachments?.some((attachment: any) => {
							return JSON.stringify(attachment) === JSON.stringify(fileReference);
						});
					});
				}).map((creditNote: any) => creditNote.reference);

				const charges = data.risolto_action[0].dunning_notice_by_reference.charges;
				const interest = data.risolto_action[0].dunning_notice_by_reference.interest;
				const enabledDamageClauses = data.risolto_action[0].custom_fields?.enabled_damage_clauses ?? null;
				const enabledReminderCosts = data.risolto_action[0].custom_fields?.enabled_reminder_costs ?? null;
				const overrideReminderCosts = data.risolto_action[0].custom_fields?.override_reminder_costs ?? null;

				const enabledOverrideReminderCosts = typeof overrideReminderCosts === 'number';

				this.dunningNoticeForm = {
					...this.dunningNoticeForm,
					selectedInvoices,
					selectedCreditNotes,
					selectedDebitNotes,
					selectedPayments,
					attachedInvoices,
					attachedCreditNotes,
					charges,
					interest,
					enabledDamageClauses,
					enabledReminderCosts,
					overrideReminderCosts,
					enabledOverrideReminderCosts
				};

				this.dunningNoticeFormState = {
					...this.dunningNoticeFormState,
					isDirty: false
				};

				return data.risolto_action[0];
			}
		},
		emailAddressFrom: {
			query: GET_EMAIL_ADDRESS_FROM,
			variables() {
				return {
					participant: this.selectedParticipantStore,
					action: this.action.reference
				};
			},
			skip() {
				return !this.action;
			},
			update: data => {
				const legalEntity = data.risolto_action[0].legal_entity_by_reference;
				const emailAddresses1 = legalEntity.email_addresses1 || [];
				const emailAddresses2 = legalEntity.email_addresses2 || [];

				const allAddresses = [...emailAddresses1, ...emailAddresses2].map(addr => ({
					email_from: addr.email_from,
					email_from_name: addr.email_from_name
				}));

				const uniqueAddressesMap = new Map();
				allAddresses.forEach(addr => {
					if (addr.email_from && !uniqueAddressesMap.has(addr.email_from)) {
						uniqueAddressesMap.set(addr.email_from, addr);
					}
				});

				return Array.from(uniqueAddressesMap.values());
			}
		}
	},
	props: {
		initialAction: { type: Object, default: () => {} },
		selectedTemplate: { type: Object, default: () => {} },
		componentType: { type: String, default: () => {} },
		metadata: { type: Object, default: () => {} },
		onActionChanged: { type: Function, default: () => {} }
	},
	data() {
		return {
			localAction: null,
			isDirty: false,
			toolbarHeight: 0,
			ismarkingAsDone: false,
			fieldDialogFormVisible: false,
			inProgressContact: undefined,
			inProgressChannelEndpoint: undefined,
			isExecutingAction: false,
			isCancelingAction: false,
			isShowActionCustomFields: false,
			isShowRecipientCustomFields: false,
			originalAction: undefined,
			retryCount: undefined,
			contacts: undefined,
			customFieldsJsonEditorData: [{}],
			recipientAddressType: undefined,
			jsonData: undefined,
			templateEditorContent: '',
			hasCoverLetter: true,
			selectedFile: undefined,
			tinymcecontent: null,
			inProgressSecentedContactAndEndpoint: undefined,
			showTinyMCEEditor: false,
			currentAttachments: undefined,
			renderedHTMLBase64: undefined,
			renderedHTMLUri: undefined,
			contactSelectField: undefined,
			currentRecipient: null,
			editRenderedHTML: false,
			showRenderedBase64: false,
			initialPlainHTML: undefined,
			emailForm: {
				from: '',
				to: '',
				cc: '',
				bcc: '',
				subject: ''
			},
			smsForm: {
				to: ''
			},
			smsFormRules: {
				to: [{ required: true, message: this.$t('validator.action.sms.to.is_required'), trigger: 'change' }]
			},
			smsToInvalid: false,
			isEditable: false,
			toAddressFull: true,
			fromAddressFull: true,
			toMailFormFull: {
				companyName: '',
				streetName: '',
				streetNumber: '',
				postBox: '',
				zipCode: '',
				city: '',
				iso_country_code: ''
			},
			toMailFormFullRules: {
				companyName: [{ required: false, trigger: 'change' }],
				streetName: [{ required: true, message: this.$t('validator.action.mail.street_name.is_required'), trigger: 'change' }],
				streetNumber: [{ required: true, message: this.$t('validator.action.is_required'), trigger: 'change' }],
				zipCode: [{ required: true, message: this.$t('validator.action.is_required'), trigger: 'change' }],
				city: [{ required: true, message: this.$t('validator.action.mail.city.is_required'), trigger: 'change' }],
				iso_country_code: [{ required: true, message: this.$t('validator.action.mail.country.is_required'), trigger: 'change' }]
			},
			toMailFormMinimal: {
				companyName: '',
				addressLine: '',
				zipCode: '',
				city: '',
				iso_country_code: ''
			},
			toMailFormMinimalRules: {
				companyName: [{ required: false, trigger: 'change' }],
				addressLine: [{ required: true, message: this.$t('validator.action.mail.street_name_number_box.is_required'), trigger: 'change' }],
				zipCode: [{ required: true, message: this.$t('validator.action.is_required'), trigger: 'change' }],
				city: [{ required: true, message: this.$t('validator.action.is_required'), trigger: 'change' }]
			},
			fromMailFormFull: {
				streetName: '',
				streetNumber: '',
				postBox: '',
				zipCode: '',
				city: '',
				iso_country_code: ''
			},
			fromMailFormMinimal: {
				addressLine: '',
				zipCode: '',
				city: '',
				iso_country_code: ''
			},
			fromPeppolForm: {
				party_name: '',
				vat_number: '',
				peppol_identifier: ''
			},
			toPeppolForm: {
				party_name: '',
				vat_number: '',
				peppol_identifier: ''
			},
			peppolDocForm: {
				document_id: '',
				document_type: ''
			},
			peppolUblAttachment: null,
			isUploadingPeppol: false,
			isValidatingPeppol: false,
			isResendingPeppol: false,
			directDebitForm: {
				document_number: '',
				mandate_id: '',
				amount: ''
			},
			channels: [],
			languages: [],
			checkAllInvoices: false,
			isIndeterminateInvoices: false,
			showDunningNoticeForm: [],
			showAdvancedOptions: [],
			dunningNoticeFormState: {
				isDirty: false,
				showCheckAllInvoices: false,
				showCheckAllCreditNotes: false,
				showCheckAllDebitNotes: false,
				showCheckAllPayments: false,
				showAllInvoices: false,
				showAllCreditNotes: false,
				showAllDebitNotes: false,
				showAllPayments: false,
				checkAllInvoices: false,
				checkAllCreditNotes: false,
				checkAllDebitNotes: false,
				checkAllPayments: false,
				checkAllInvoicesAttachment: false,
				checkAllCreditNotesAttachment: false,
				isIndeterminateInvoices: false,
				isIndeterminateCreditNotes: false,
				isIndeterminateDebitNotes: false,
				isIndeterminatePayments: false,
				isIndeterminateInvoicesAttachment: false,
				isIndeterminateCreditNotesAttachment: false
			},
			dunningNoticeForm: {
				channel: Risolto_Channel_Enum.Email,
				selectedInvoices: [],
				attachedInvoices: [],
				selectedCreditNotes: [],
				attachedCreditNotes: [],
				selectedDebitNotes: [],
				attachedDebitNotes: [],
				selectedPayments: [],
				attachedPayments: [],
				enabledOverrideReminderCosts: false,
				enabledDamageClauses: null,
				enabledReminderCosts: null,
				overrideReminderCosts: null,
				reminderCosts: 0,
				charges: 0,
				interest: 0
			},
			overrideReminderCostsInput: '0',
			dunningNoticeFormRules: {
				//
			},
			userInterfaceDetails: {
				custom_field_definitions: [],
				disallowed_channels: [],
				disallowed_message_codes: []
			},
			mainPanelWidth: 17,
			rightPanelWidth: 7,
			isUploadingAttachment: false,
			settingName: 'document_selection'
		};
	},
	computed: {
		action() {
			return this.localAction;
		},
		...mapGetters([
			'env',
			'beta',
			'pdfPreviewDialogVisible',
			'selectedParticipantStore',
			'selectedRecipient',
			'selectedContact',
			'selectedChannelEndpoint'
		]),
		...mapState({
			actionDetailRightPanelCollapsed: (state: any) => state.actions.actionDetailRightPanelCollapsed
		}),
		isHandlingAction() {
			return this.isExecutingAction || this.isCancelingAction;
		},
		countries() {
			let countries = COUNTRIES;
			// Translate country names
			countries.forEach(country => {
				country.name = this.$translate.iso_country_code(country.code);
			});
			// Sort by country name
			countries.sort((a, b) => {
				return a.name.localeCompare(b.name);
			});

			return countries;
		},
		availableChannels() {
			return this.action && this.action.metadata.channel_templates ? Object.keys(this.action.metadata.channel_templates) : [];
		},
		apiKey() {
			return process.env.VUE_APP_TINYMCE_API_KEY;
		},
		showAttachmentBlock() {
			if ((this.metadata && this.metadata.channel === 'EMAIL') || (this.metadata && this.metadata.channel === 'MAIL')) {
				return true;
			} else if ((this.action && this.action.channel === 'EMAIL') || (this.action && this.action.channel === 'MAIL')) {
				return true;
			} else {
				return false;
			}
		},
		processedTo() {
			return this.emailForm?.to ? this.emailForm.to
				.split(';')
				.flatMap(x => x.split(','))
				.map(x => x.trim())
				.filter(x => this.validateEmail(x))
				.find(() => true) // Returns first element of array
				: undefined;
		},
		processedCC() {
			return this.emailForm.cc
				.split(';')
				.flatMap(x => x.split(','))
				.map(x => x.trim())
				.filter(x => this.validateEmail(x));
		},
		processedBCC() {
			return this.emailForm.bcc
				.split(';')
				.flatMap(x => x.split(','))
				.map(x => x.trim())
				.filter(x => this.validateEmail(x));
		},
		editable() {
			return this.action.status !== 'DONE' && this.action.status !== 'CANCELED' && this.action.status !== 'PROCESSING';
		},
		canBeExecutedManually() {
			const action = this.action;

			if (this.userInterfaceDetails && this.userInterfaceDetails.disallowed_channels && this.userInterfaceDetails.disallowed_channels.includes(action.channel)) {
				return {
					valid: false,
					error: this.$t('validator.action.general.channel_disallowed')
				};
			}
			if (this.userInterfaceDetails && this.userInterfaceDetails.disallowed_message_codes && this.userInterfaceDetails.disallowed_message_codes.includes(action.message_code)) {
				return {
					valid: false,
					error: this.$t('validator.action.general.message_code_disallowed')
				};
			}

			return {
				valid: true
			};
		},
		canEnableRegistered() {
			return this.editable && hasLicenseAndPermission(
				this.$store.state.risolto.licences[this.selectedParticipantStore],
				this.$store.state.risolto.permissions[this.selectedParticipantStore],
				'CORE',
				'ACTION_ENABLE_REGISTERED'
			);
		},
		suggestedTo() {
			const suggestions = new Set();
			if (this.action && this.action.metadata && this.action.metadata.channels[0]) {
				const metadata = this.action.metadata.channels[0];
				if (metadata.to) {
					suggestions.add(metadata.to);
				}
				if (metadata.suggested_to && metadata.suggested_to.length > 0) {
					metadata.suggested_to.forEach(item => suggestions.add(item));
				}
			}
			suggestions.delete(this.emailForm.to);
			return Array.from(suggestions);
		},
		formValid() {
			let valid = true;
			switch (this.componentType) {
				case 'EMAIL':
					if (this.emailForm.from == null || this.emailForm.from === '') {
						return false;
					}
					if (this.emailForm.to == null || this.emailForm.to === '') {
						return false;
					}
					if (this.emailForm.subject == null || this.emailForm.subject === '') {
						return false;
					}
					if (!Utils.isValidEmail(this.emailForm.to) || !Utils.isValidEmail(this.emailForm.from)) {
						return false;
					}

					return valid;
				case 'MAIL':
					if (this.toAddressFull) {
						if (this.toMailFormFull.streetName === null || this.toMailFormFull.streetName === '') {
							return false;
						}
						if (this.toMailFormFull.streetNumber === null || this.toMailFormFull.streetNumber === '') {
							return false;
						}
						if (this.toMailFormFull.zipCode === null || this.toMailFormFull.zipCode === '') {
							return false;
						}
						if (this.toMailFormFull.city === null || this.toMailFormFull.city === '') {
							return false;
						}
						if (this.toMailFormFull.iso_country_code === null || this.toMailFormFull.iso_country_code === '') {
							return false;
						}
					} else {
						if (this.toMailFormMinimal.addressLine === null || this.toMailFormMinimal.addressLine === '') {
							return false;
						}
						if (this.toMailFormMinimal.zipCode === null || this.toMailFormMinimal.zipCode === '') {
							return false;
						}
						if (this.toMailFormMinimal.city === null || this.toMailFormMinimal.city === '') {
							return false;
						}
						if (this.toMailFormMinimal.iso_country_code === null || this.toMailFormMinimal.iso_country_code === '') {
							return false;
						}
					}

					return valid;

				case 'SMS':
					if (this.smsForm.to == null || this.smsForm.to === '') {
						return false;
					}

					return valid;

				default:
					return valid;
			}
		},
		invoices() {
			let invoices = this.recipientRecord.invoice_documents.map((invoice) => {
				return {
					...invoice,
					hasPdfAttachment: (invoice.file_references || []).filter((file: any) => AttachmentHelper.isPdf(file)).length > 0.00
				};
			});

			if (this.dunningNoticeFormState.showAllInvoices) {
				return invoices;
			} else {
				return invoices.filter(invoice => invoice.open_amount > 0.00);
			}
		},
		creditNotes() {
			let creditNotes = this.recipientRecord.credit_notes.map((creditNote) => {
				return {
					...creditNote,
					hasPdfAttachment: (creditNote.file_references || []).filter((file: any) => AttachmentHelper.isPdf(file)).length > 0.00
				};
			});

			if (this.dunningNoticeFormState.showAllCreditNotes) {
				return creditNotes;
			} else {
				return creditNotes.filter(creditNote => creditNote.open_amount > 0.00);
			}
		},
		debitNotes() {
			if (this.dunningNoticeFormState.showAllDebitNotes) {
				return this.recipientRecord.debit_notes;
			} else {
				return this.recipientRecord.debit_notes.filter(debitNote => debitNote.open_amount > 0.00);
			}
		},
		payments() {
			if (this.dunningNoticeFormState.showAllPayments) {
				return this.recipientRecord.payment_documents;
			} else {
				return this.recipientRecord.payment_documents.filter(payment => payment.open_amount > 0.00);
			}
		},
		hasNoDocuments() {
			return (
				(this.dunningNoticeForm.selectedInvoices || []) + (this.dunningNoticeForm.selectedCreditNotes || []) + (this.dunningNoticeForm.selectedDebitNotes || []) + (this.dunningNoticeForm.selectedPayments || [])
			).length === 0;
		},
		hasNoSelectedInvoicesWithAttachments() {
			return this.invoices.filter((document: { reference: any; hasPdfAttachment: any; }) => {
				return this.dunningNoticeForm.selectedInvoices.includes(document.reference) && document.hasPdfAttachment;
			}).length === 0;
		},
		hasNoSelectedCreditNotesWithAttachments() {
			return this.creditNotes.filter((document: { reference: any; hasPdfAttachment: any; }) => {
				return this.dunningNoticeForm.selectedCreditNotes.includes(document.reference) && document.hasPdfAttachment;
			}).length === 0;
		},
		modalContentStyle() {
			// Calculate the modal content height
			return `padding-top: ${this.toolbarHeight}px`;
		},
		canChangeTemplate() {
			return this.action.source === 'MANUAL' && hasLicenseAndPermission(
				this.$store.state.risolto.licences[this.selectedParticipantStore],
				this.$store.state.risolto.permissions[this.selectedParticipantStore],
				'CORE',
				'UI_ACTION_CHANGE_TEMPLATE'
			);
		},
		isManualAction() {
			return this.action && this.action.source === Risolto_Action_Source_Enum.Manual;
		},
		hasEditManualActionCostsPermission(): boolean {
			const licences = this.$store.state.risolto.licences[this.selectedParticipantStore];
			const permissions = this.$store.state.risolto.permissions[this.selectedParticipantStore];

			Logging.info('Permissions', permissions);

			return hasLicenseAndPermission(
				licences,
				permissions,
				'CORE',
				'UI_EDIT_MANUAL_ACTION_COSTS'
			);
		},
		peppolUploadUrl() {
			return `https://${process.env.VUE_APP_API_ENDPOINT}/operator-fe/action/peppol/upload-ubl-xml`;
		},
		uploadHeaders() {
			return {
				Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}`
			};
		},
		peppolUploadData() {
			return {
				participant: this.selectedParticipantStore,
				action: this.action.reference
			};
		}
	},
	watch: {
		env() {
			Logging.log('this.env: ', this.env);
		},
		selectedChannelEndpoint(channelEndpoint: Risolto_Channel_Endpoint | null) {
			if (channelEndpoint == null) {
				return;
			}

			this.handleChannelEndpointChange(channelEndpoint);
		},
		'dunningNoticeFormState.showAllInvoices'(value) {
			this.updateCheckAllInvoices();
		},
		'dunningNoticeFormState.showAllCreditNotes'(value) {
			this.updateCheckAllCreditNotes();
		},
		'dunningNoticeFormState.showAllDebitNotes'(value) {
			this.updateCheckAllDebitNotes();
		},
		'dunningNoticeFormState.showAllPayments'(value) {
			this.updateCheckAllPayments();
		},
		'dunningNoticeForm.selectedInvoices' (value) {
			this.updateCheckAllInvoices();
		},
		'dunningNoticeForm.selectedCreditNotes' (value) {
			this.updateCheckAllCreditNotes();
		},
		'dunningNoticeForm.selectedDebitNotes' (value) {
			this.updateCheckAllDebitNotes();
		},
		'dunningNoticeForm.selectedPayments' (value) {
			this.updateCheckAllPayments();
		},
		'dunningNoticeForm.attachedInvoices' (value) {
			this.updateAttachAllInvoices();
		},
		'dunningNoticeForm.attachedCreditNotes' (value) {
			this.updateAttachAllCreditNotes();
		},
		actionDetailRightPanelCollapsed() {
			this.evaluateLayout();
		},
		overrideReminderCostsInput(value: string) {
			const REGEX = /^\d+,{0,1}\d{0,2}$/;

			if (value === '') {
				this.overrideReminderCostsInput = '0';
				return;
			}

			// Keep digits and commas
			let normalized = value.replace(/[^\d,]/g, '');

			// Remove duplicates ","
			let parts = normalized.split(',');
			if (parts.length > 2) {
				normalized = parts[0] + ',' + parts.slice(1).join('');
			}

			// ",5" -> "0,5"
			if (normalized.startsWith(',')) {
				normalized = `0${normalized}`;
			}

			// Remove unnecessary leading zeros but keep "0," cases
			if (/^0\d/.test(normalized)) {
				normalized = normalized.replace(/^0+/, '');
			}

			// Limit the digits up to two decimals
			parts = normalized.split(',');
			const integer = parts[0] ?? '';
			const decimals = parts[1]?.slice(0, 2) ?? '';
			normalized = decimals !== '' || normalized.includes(',')
				? `${integer},${decimals}`
				: integer;

			this.overrideReminderCostsInput = normalized;

			const numberValue = Number(value.replace(',', '.'));
			this.dunningNoticeForm.overrideReminderCosts = isNaN(numberValue) ? null : numberValue;
		}
	},
	created() {
		// Deep clone the initial action into our local working copy — only once
		this.localAction = JSON.parse(JSON.stringify(this.initialAction));
		this.actionDetailLogic();
		this.evaluateLayout();
	},
	mounted() {
		this.toolbarHeight = this.$refs.toolbar.offsetHeight;
	},
	methods: {
		async handleSelectEndpoint(contact: Risolto_Contact, channelEndpoint: Risolto_Channel_Endpoint) {
			if (this.disabledContacts(contact) || this.disabledEndpoint(channelEndpoint) || this.action.channel !== channelEndpoint.channel) {
				this.$notify({
					title: this.$t('warning'),
					message: this.$t('contact_no_valid_channel_endpoint'),
					type: 'warning'
				});
				return;
			}

			this.$confirm(this.$t('Contacts.confirm_select_contact'), {
				confirmButtonText: this.$t('Ok'),
				cancelButtonText: this.$t('cancel'),
				type: 'warning'
			}).then(() => {
				if (this.action.channel === 'EMAIL') {
					this.fieldDialogFormVisible = true;
					this.inProgressContact = contact;
					this.inProgressChannelEndpoint = channelEndpoint;
				} else {
					this.$store.commit('risolto/SET_SELECTED_CHANNEL_ENDPOINT', channelEndpoint);
					this.$store.commit('risolto/SET_SELECTED_CONTACT', contact);
				}
			}).catch(() => {
				// do nothing
			});
		},
		async continueHandleSelectEndpointForEmail(field, contact: Risolto_Contact, channelEndpoint: Risolto_Channel_Endpoint) {
			this.fieldDialogFormVisible = false;
			switch (field) {
				case 'to':
					this.emailForm.to = channelEndpoint.endpoint;
					this.$store.commit('risolto/SET_SELECTED_CHANNEL_ENDPOINT', channelEndpoint);
					this.$store.commit('risolto/SET_SELECTED_CONTACT', contact);
					break;
				case 'cc':
					this.emailForm.cc = this.emailForm.cc + ',' + channelEndpoint.endpoint;
					break;
				case 'bcc':
					this.emailForm.bcc = this.emailForm.bcc + ',' + channelEndpoint.endpoint;
					break;
				default:
					break;
			}
		},
		handleChannelChange(channel) {
			if (this.action.metadata.body !== null || this.action.metadata.channels[0].body !== null) {
				this.$confirm(this.$t('actionDetail.body_change'), this.$t('warning'), {
					confirmButtonText: 'OK',
					cancelButtonText: this.$t('cancel'),
					type: 'warning'
				})
					.then(() => {
						this.changeChannel(channel);
					})
					.catch(() => {
						return;
					});
			} else {
				this.changeChannel(channel);
			}
		},
		async changeChannel(channel) {
			// Deep clone the channel template (it's nested inside localAction, so needs its own copy)
			const copiedChannel = JSON.parse(JSON.stringify(this.action.metadata.channel_templates[channel]));

			this.localAction.channel = channel;
			this.localAction.metadata.subject = null;
			this.localAction.metadata.body = null;
			this.localAction.metadata.channels = [copiedChannel];

			this.$store.commit('risolto/SET_COMPONENT_TYPE_STORE', channel);
			this.onActionChanged(this.localAction);

			// Reset render state so saveChanges doesn't write old body back to channels[0].body
			this.tinymcecontent = null;
			this.showTinyMCEEditor = false;

			// Populate form fields from new channel BEFORE saving (prevents stale form data)
			this.populateChannelFields();

			// Persist to backend (template renderer fetches from DB)
			await this.saveChanges();
		},
		handleTemplateChange(template_id) {
			if (this.action.metadata.body !== null || this.action.metadata.channels[0].body !== null) {
				this.$confirm(this.$t('actionDetail.body_change'), this.$t('warning'), {
					confirmButtonText: 'OK',
					cancelButtonText: this.$t('cancel'),
					type: 'warning'
				})
					.then(() => {
						this.changeTemplate(template_id);
					})
					.catch(() => {
						return;
					});
			} else {
				this.changeTemplate(template_id);
			}
		},
		async changeTemplate(template_id) {
			this.localAction.metadata.subject = null;
			this.localAction.metadata.body = null;
			this.localAction.metadata.channels[0].subject = null;
			this.localAction.metadata.channels[0].body = null;
			this.localAction.metadata.channels[0].template.template_id = template_id;

			if (this.componentType === 'EMAIL') this.emailForm.subject = null;

			this.isDirty = true;
			this.onActionChanged(this.localAction);

			// Reset render state so saveChanges doesn't write old body back to channels[0].body
			this.tinymcecontent = null;
			this.showTinyMCEEditor = false;

			// Persist to backend (template renderer fetches from DB)
			await this.saveChanges();
		},
		preSelectedContacts(contact) {
			const endpoints = contact.active_endpoints.filter((endpoint) => {
				return endpoint.reference === this.action.channel_endpoint;
			});
			if (endpoints.length > 0) {
				return true;
			}

			return false;
		},
		disabledEndpoint(endpoint) {
			if (!endpoint) {
				return false;
			}
			if (endpoint.deactivated_at) {
				return true;
			}
		},
		disabledContacts(contact) {
			if (!contact) {
				return false;
			}
			const endpoints = contact.active_endpoints.filter((endpoint) => {
				return endpoint.channel === this.action.channel;
			});
			if (endpoints.length === 0) {
				return true;
			}

			return false;
		},
		handleChannelEndpointChange(endpoint) {
			const contact = this.selectedContact;

			this.localAction.channel_endpoint = endpoint.reference;
			if (contact && this.localAction.metadata.channels[0].hasOwnProperty('template')) {
				this.localAction.metadata.channels[0].template.locale = contact.locale.substring(0, 2);
			}

			this.isDirty = true;
			this.onActionChanged(this.localAction);

			this.$nextTick(() => {
				this.useChannelEndpoint('to', endpoint.endpoint, endpoint.endpoint_details);
				this.useChannelEndpoint('from');
			});
		},
		handleLanguageChange(language) {
			if (this.action.metadata.body !== null || this.action.metadata.channels[0].body !== null) {
				this.$confirm(this.$t('actionDetail.body_change'), this.$t('warning'), {
					confirmButtonText: 'OK',
					cancelButtonText: this.$t('cancel'),
					type: 'warning'
				})
					.then(() => {
						this.changeLanguage(language);
					})
					.catch(() => {
						return;
					});
			} else {
				this.changeLanguage(language);
			}
		},
		async changeLanguage(language) {
			if (this.localAction.metadata.channels[0].hasOwnProperty('template')) {
				this.localAction.metadata.subject = null;
				this.localAction.metadata.body = null;
				this.localAction.metadata.channels[0].subject = null;
				this.localAction.metadata.channels[0].body = null;
				this.localAction.metadata.channels[0].template.locale = language;
			}
			if (this.componentType === 'EMAIL') this.emailForm.subject = null;

			this.isDirty = true;
			this.onActionChanged(this.localAction);

			// Reset render state so saveChanges doesn't write old body back to channels[0].body
			this.tinymcecontent = null;
			this.showTinyMCEEditor = false;

			// Persist to backend (template renderer fetches from DB)
			await this.saveChanges();
		},
		useSubject() {
			if (this.action && this.action.metadata && this.action.metadata.channels && this.action.metadata.channels.length > 0 && this.action.metadata.channels[0].subject) {
				this.emailForm.subject = this.action.metadata.channels[0].subject;
				this.isDirty = true;
			}
		},
		useChannelEndpoint(val, endpoint, endpoint_details) {
			this.isDirty = true;
			if (val === 'to') {
				if (!this.action.channel_endpoint_by_reference && !endpoint) {
					return;
				}
				if (this.componentType === 'MAIL') {
					var to = endpoint_details ? endpoint_details : this.action.channel_endpoint_by_reference.endoint_details;
					try {
						to = to.address ? to.address : to;
					} catch (e) {
						// pass
					}
					if (to) {
						let address = to;

						if (address && address.street_name_number_box) {
							this.toAddressFull = false;
							this.toMailFormMinimal.companyName = address.company_name ? address.company_name : '';
							if (address.street_name_number_box) {
								this.toMailFormMinimal.addressLine = `${address.street_name_number_box}`;
							} else {
								this.toMailFormMinimal.addressLine = 'N/A';
							}

							this.toMailFormMinimal.city = address.city ? address.city : '';
							this.toMailFormMinimal.iso_country_code = address.iso_country_code ? address.iso_country_code : '';

							if (address.zip_code) {
								this.toMailFormMinimal.zipCode = address.zip_code;
							} else if (address.postal_code) {
								this.toMailFormMinimal.zipCode = address.postal_code;
							}
						} else if (address) {
							this.toAddressFull = true;

							this.toMailFormFull.companyName = address.company_name ? address.company_name : '';
							this.toMailFormFull.streetName = address.street_name ? address.street_name : '';
							this.toMailFormFull.streetNumber = address.street_number ? address.street_number : '';
							this.toMailFormFull.postBox = address.post_box ? address.post_box : '';
							this.toMailFormFull.city = address.city ? address.city : '';
							this.toMailFormFull.iso_country_code = address.iso_country_code ? address.iso_country_code : '';

							if (address.zip_code) {
								this.toMailFormFull.zipCode = address.zip_code;
							} else if (address.postal_code) {
								this.toMailFormFull.zipCode = address.postal_code;
							}
						}
					}
				} else if (this.componentType === 'EMAIL') {
					try {
						this.emailForm.to = endpoint || (this.action.channel_endpoint_by_reference.endpoint || '');
					} catch (e) {
						// ignore
						return;
					}
				} else if (this.componentType === 'SMS' && this.action.channel_endpoint_by_reference) {
					try {
						this.smsForm.to = endpoint || (this.action.channel_endpoint_by_reference.endpoint || '');
					} catch (e) {
						// ignore
						return;
					}
				}
			} else if (val === 'cc') {
				if (this.componentType !== 'EMAIL') {
					return;
				}
				try {
					this.emailForm.cc = this.action.metadata.channels[0].cc;
				} catch (e) {
					// ignore
					return;
				}
			} else if (val === 'bcc') {
				if (this.componentType !== 'EMAIL') {
					return;
				}
				try {
					this.emailForm.bcc = this.action.metadata.channels[0].bcc;
				} catch (e) {
					// ignore
					return;
				}
			} else if (val === 'from') {
				if (!this.action.legal_entity_by_reference.outbound_channel) {
					return;
				}

				if (this.componentType === 'MAIL') {
					const from = this.action.legal_entity_by_reference.outbound_channel.postal_address;
					let address = from.address ? from.address : from;

					if (address && address.street_name_number_box) {
						this.fromAddressFull = false;
						if (address.street_name_number_box) {
							this.fromMailFormMinimal.addressLine = `${address.street_name_number_box}`;
						} else {
							this.fromMailFormMinimal.addressLine = 'N/A';
						}

						this.fromMailFormMinimal.city = address.city ? address.city : '';
						this.fromMailFormMinimal.iso_country_code = address.iso_country_code ? address.iso_country_code : '';

						if (address.zip_code) {
							this.fromMailFormMinimal.zipCode = address.zip_code;
						} else if (address.postal_code) {
							this.fromMailFormMinimal.zipCode = address.postal_code;
						}
					} else if (address) {
						this.fromAddressFull = true;

						this.fromMailFormFull.streetName = address.street_name ? address.street_name : '';
						this.fromMailFormFull.streetNumber = address.street_number ? address.street_number : '';
						this.fromMailFormFull.postBox = address.post_box ? address.post_box : '';
						this.fromMailFormFull.city = address.city ? address.city : '';
						this.fromMailFormFull.iso_country_code = address.iso_country_code ? address.iso_country_code : '';

						if (address.zip_code) {
							this.fromMailFormFull.zipCode = address.zip_code;
						} else if (address.postal_code) {
							this.fromMailFormFull.zipCode = address.postal_code;
						}
					}
				} else if (this.componentType === 'EMAIL') {
					try {
						this.emailForm.from = this.action.metadata.channels[0]._from;
					} catch (e) {
						// ignore
						return;
					}
				}
			}
		},
		async handleDownloadSample() {
			try {
				this.$store.commit('risolto/SET_FULL_SCREEN_LOADING', true);

				let queryParameters = '?redirect=false';
				const requestBody = {};
				if (this.renderedHTMLUri) {
					// Render from the temporary URI location
					queryParameters = `${queryParameters}&uri=${encodeURIComponent(this.renderedHTMLUri)}`;
				} else {
					// Render the body data
					if (this.tinymcecontent === null) {
						throw new Error('no_content');
					}
					// @ts-ignore
					requestBody.base64 = Base64.encode(this.tinymcecontent);
				}
				const requestUri = `https://${process.env.VUE_APP_API_ENDPOINT}/templating/html2pdf/v1${queryParameters}`;
				const response = await axios.post(requestUri, requestBody, {
					headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
				});

				Logging.log(response);
				Logging.log(response.data.uri);

				var windowReference = window.open();
				// @ts-ignore
				windowReference.location = response.data.uri;
				this.$store.commit('risolto/SET_FULL_SCREEN_LOADING', false);
			} catch (e) {
				Logging.error(e);
				this.$store.commit('risolto/SET_FULL_SCREEN_LOADING', false);
				this.$notify({
					title: this.$t('warning'),
					message: this.$t('somethingWentWrong'),
					type: 'error'
				});
			}
		},
		async onSaveChanges() {
			await this.saveChanges();
		},
		async saveChanges() {
			let action: Risolto_Action = this.action;

			let variables = { metadata: {} as Action_Metadata, action: this.action.reference, channel: this.action.channel };

			const channelEndpoint = action.channel_endpoint;
			if (channelEndpoint) {
				await actionUpdateChannelEndpoint(action.reference, channelEndpoint);
			}

			if (action.channel === 'SMS') {
				variables.metadata = action.metadata;
				let channelMetadata: Sms_Metadata = action.metadata.channels[0];
				channelMetadata.to = this.smsForm.to.replace(/ /g, '');
				if (this.tinymcecontent) {
					channelMetadata.body = Base64.encode(this.tinymcecontent);
				}
				variables.metadata.channels[0] = channelMetadata;
			} else if (action.channel === 'EMAIL') {
				variables.metadata = action.metadata;
				let channelMetadata: Email_Metadata = action.metadata.channels[0];
				channelMetadata.to = this.processedTo;
				channelMetadata.cc = this.processedCC;
				channelMetadata.bcc = this.processedBCC;
				channelMetadata._from = this.emailForm.from;
				channelMetadata.subject = this.emailForm.subject;
				if (this.tinymcecontent) {
					channelMetadata.body = Base64.encode(this.tinymcecontent);
				}
			} else if (action.channel === 'MAIL') {
				variables.metadata = action.metadata;
				let channelMetadata: Mail_Metadata = action.metadata.channels[0];

				let toAddress: Address = {};
				if (this.toAddressFull) {
					toAddress.company_name = this.toMailFormFull.companyName;
					toAddress.street_name = this.toMailFormFull.streetName;
					toAddress.street_number = this.toMailFormFull.streetNumber;
					toAddress.post_box = this.toMailFormFull.postBox;
					toAddress.city = this.toMailFormFull.city;
					toAddress.zip_code = this.toMailFormFull.zipCode;
					toAddress.iso_country_code = this.toMailFormFull.iso_country_code;
				} else {
					toAddress.company_name = this.toMailFormMinimal.companyName;
					toAddress.street_name_number_box = this.toMailFormMinimal.addressLine;
					toAddress.city = this.toMailFormMinimal.city;
					toAddress.zip_code = this.toMailFormMinimal.zipCode;
					toAddress.iso_country_code = this.toMailFormMinimal.iso_country_code;
				}
				channelMetadata.to.address = toAddress;

				// We leave _from untouched for now
				// let fromAddress: Address = {};
				// if (this.fromAddressFull) {
				// 	fromAddress.street_name = this.fromMailFormFull.streetName;
				// 	fromAddress.street_number = this.fromMailFormFull.streetNumber;
				// 	fromAddress.post_box = this.fromMailFormFull.postBox;
				// 	fromAddress.city = this.fromMailFormFull.city;
				// 	fromAddress.zip_code = this.fromMailFormFull.zipCode;
				// 	fromAddress.iso_country_code = this.fromMailFormFull.iso_country_code;
				// } else {
				// 	fromAddress.street_name_number_box = this.fromMailFormMinimal.addressLine;
				// 	fromAddress.city = this.fromMailFormMinimal.city;
				// 	fromAddress.zip_code = this.fromMailFormMinimal.zipCode;
				// 	fromAddress.iso_country_code = this.fromMailFormMinimal.iso_country_code;
				// }
				// channelMetadata._from.address = fromAddress;

				if (action.source !== Risolto_Action_Source_Enum.Dunning) {
					channelMetadata.add_front_page = true;
				}
				if (!channelMetadata.template) {
					channelMetadata.add_front_page = true;
				}
			} else {
				variables.metadata = action.metadata;
				Logging.log(`TODO: Write handler for ${action.channel} channel in onSaveChanges`);
			}

			Logging.log('variables.metadata.channels[0]: ', variables.metadata.channels[0]);

			if (this.dunningNoticeFormState.isDirty) {
				variables.metadata.channels[0].body = null;
			}

			await this.$apollo
				.mutate({
					mutation: UPDATE_ACTION_METADATA,
					variables: variables
				})
				.then(result => {
					Logging.log('UPDATE_ACTION_METADATA: ', result);
					this.isDirty = false;
					this.$notify({
						title: this.$t('success'),
						message: this.$t('saved'),
						type: 'success'
					});
				})
				.catch(e => {
					Logging.error('Err in saveChanges/update_action_metadata', e);
				});

			if (this.dunningNoticeFormState.isDirty) {
				try {
					const charges = this.dunningNotice.dunning_notice_by_reference.charges;
					const interest = this.dunningNotice.dunning_notice_by_reference.interest;

					const invoices = this.recipientRecord.invoice_documents.filter((invoice: any) => this.dunningNoticeForm.selectedInvoices.indexOf(invoice.reference) !== -1).map((invoice: any) => {
						return {
							reference: invoice.reference,
							attachment: this.dunningNoticeForm.attachedInvoices.indexOf(invoice.reference) !== -1
						};
					});
					const creditNotes = this.recipientRecord.credit_notes.filter((creditNote: any) => this.dunningNoticeForm.selectedCreditNotes.indexOf(creditNote.reference) !== -1).map((creditNote: any) => {
						return {
							reference: creditNote.reference,
							attachment: this.dunningNoticeForm.attachedCreditNotes.indexOf(creditNote.reference) !== -1
						};
					});
					const debitNotes = this.recipientRecord.debit_notes.filter((debitNote: any) => this.dunningNoticeForm.selectedDebitNotes.indexOf(debitNote.reference) !== -1).map((debitNote: any) => {
						return {
							reference: debitNote.reference,
							attachment: false
						};
					});
					const payments = this.recipientRecord.payment_documents.filter((payment: any) => this.dunningNoticeForm.selectedPayments.indexOf(payment.reference) !== -1).map((payment: any) => {
						return {
							reference: payment.reference,
							attachment: false
						};
					});

					const attachedInvoices = this.recipientRecord.invoice_documents
						.filter(invoice => this.dunningNoticeForm.attachedInvoices.includes(invoice.reference))
						.map(invoice => invoice.file_references.filter(file => AttachmentHelper.isPdf(file)));
					const attachedCreditNotes = this.recipientRecord.credit_notes
						.filter(creditNote => this.dunningNoticeForm.attachedCreditNotes.includes(creditNote.reference))
						.map(creditNote => creditNote.file_references.filter(file => AttachmentHelper.isPdf(file)));

					await actionUpdateDunningNotice(
						this.selectedParticipantStore,
						action.legal_entity,
						action.recipient,
						action.reference,
						invoices,
						creditNotes,
						debitNotes,
						payments,
						charges,
						interest,
						this.dunningNoticeForm.enabledDamageClauses,
						this.dunningNoticeForm.enabledReminderCosts,
						this.dunningNoticeForm.overrideReminderCosts,
						action.source
					);

					// Dunning notice updates impact template variables and require a fresh render
					this.action.metadata.body = null;
					this.action.metadata.channels[0].body = null;

					this.dunningNoticeFormState = {
						...this.dunningNoticeFormState,
						isDirty: false
					};

					action.metadata.attachments = attachedCreditNotes.concat(attachedInvoices).flat();
				} catch (e) {
					this.$notify({
						title: this.$t('somethingWentWrong'),
						message: this.$t('somethingWentWrong'),
						type: 'error'
					});
				}
			}

			this.resetAndRenderTemplate();

			// Sync changes back to Vuex (ActionTable grid updates reactively)
			this.onActionChanged(this.localAction);
		},
		onInput() {
			// TODO: check if this should be in the store
			this.isDirty = true;
		},
		onSmsInput() {
			this.isDirty = true;
			// vue-tel-input doesn't emit el-form events, so trigger validation manually
			this.$nextTick(() => {
				const form = this.$refs.smsForm;
				if (form?.fields?.length) {
					form.validateField('to');
				}
			});
		},
		onSmsValidate(value) {
			if (!value.valid) {
				this.smsToInvalid = true;
			} else {
				this.smsToInvalid = false;
			}
		},
		validateEmail(email) {
			return Utils.isValidEmail(email);
		},
		saveAttachment() {},
		handleCloseTemplateEditor() {
			this.templateEditorContent = '';
			this.templateEditorVisible = false;
		},
		removeSelectedFile() {
			this.selectedFile = undefined;
			let file = document.getElementById('file');
			// @ts-ignore
			document.getElementById('file').value = '';
		},
		handleFileChange() {
			// console.log('document.getElementById: ', document.getElementById('file').files[0]);
			// @ts-ignore
			this.selectedFile = document.getElementById('file').files[0];
		},
		handleAttachmentAdded(newAttachment: S3_Attachment) {
			// Update local state
			this.currentAttachments.push(newAttachment);

			// Update action object's metadata to ensure it's not lost during saveChanges
			if (!this.action.metadata.attachments) {
				this.action.metadata.attachments = [];
			}
			this.action.metadata.attachments.push(newAttachment);

			// Mark as dirty to ensure changes are saved
			this.isDirty = true;

			const variables = {
				metadata: {
					...this.action.metadata
				},
				action: this.action.reference,
				channel: this.action.channel
			};

			this.$apollo
				.mutate({
					mutation: UPDATE_ACTION_METADATA,
					variables: variables
				})
				.then(result => {
					// Update localAction with server-confirmed metadata
					if (result.data?.update_risolto_action?.updated?.[0]) {
						this.localAction.metadata = result.data.update_risolto_action.updated[0].metadata;
					}

					// Sync to Vuex (ActionTable grid updates reactively)
					this.onActionChanged(this.localAction);

					this.$notify({
						title: this.$t('success'),
						message: this.$t('attachmentAdded'),
						type: 'success'
					});
				});
		},
		handleRemoveAttachment(attachment: Attachment) {
			const _this = this;

			// Updates the local state and triggers the event listener (watch) on action
			if (Array.isArray(_this.currentAttachments)) {
				_this.currentAttachments = _this.currentAttachments.filter((att) => {
					return att.name !== attachment.name;
				});
			}
			if (Array.isArray(_this.action.metadata.attachments)) {
				_this.action.metadata.attachments = _this.action.metadata.attachments.filter((att) => {
					return att.name !== attachment.name;
				});
			}
			if (Array.isArray(_this.action.metadata.channels?.[0]?.attachments)) {
				_this.action.metadata.channels[0].attachments = _this.action.metadata.channels[0].attachments.filter((att) => {
					return att.name !== attachment.name;
				});
			}

			// Mark as dirty to ensure changes are saved
			_this.isDirty = true;

			// Ensure channels structure is preserved
			const metadataToSend = {
				..._this.action.metadata,
				attachments: _this.action.metadata.attachments || []
			};
			if (_this.action.metadata.channels && _this.action.metadata.channels[0]) {
				metadataToSend.channels = [{
					..._this.action.metadata.channels[0],
					attachments: _this.action.metadata.channels[0].attachments || []
				}];
			}

			const variables = {
				metadata: metadataToSend,
				action: _this.action.reference,
				channel: _this.action.channel
			};

			_this.$apollo
				.mutate({
					mutation: UPDATE_ACTION_METADATA,
					variables: variables
				})
				.then(result => {
					// Update localAction with server-confirmed metadata
					if (result.data?.update_risolto_action?.updated?.[0]) {
						_this.localAction.metadata = result.data.update_risolto_action.updated[0].metadata;
						_this.currentAttachments = [
							...(_this.localAction.metadata?.attachments || []),
							...(_this.localAction.metadata?.channels?.[0]?.attachments || [])
						];
					}

					// Sync to Vuex (ActionTable grid updates reactively)
					_this.onActionChanged(_this.localAction);

					_this.$notify({
						title: _this.$t('success'),
						message: _this.$t('attachmentRemoved'),
						type: 'success'
					});
				});
		},
		/** Run once on dialog open — parses metadata, fetches recipient/contacts, then populates forms + renders */
		actionDetailLogic() {
			Logging.log('ActionDetail.actionDetailLogic');
			if (!this.selectedParticipantStore || !this.action) return;

			Logging.log('this.action in actionDetailLogic: ', this.action);

			// Fetch original action (for retries)
			this.retryCount = this.action.retry_count;
			if (this.action.original_action) {
				this.$apollo
					.query({
						query: GET_ACTION,
						variables: {
							participant: this.action.participant,
							action: this.action.original_action
						}
					})
					.then(result => {
						Logging.log('ActionDetail.actionDetailLogic - GET_ACTION : ', result.data);
						this.originalAction = result.data.action;
					})
					.catch(e => {
						Logging.error('error in geting original action:', e);
					});
			}

			// Parse metadata if stored as string
			if (typeof this.action.metadata === 'string') {
				const parsedMetadata = JSON.parse(this.action.metadata);
				this.action.metadata = parsedMetadata;
				this.jsonData = parsedMetadata;
			}

			Logging.log('this.action.legal_entity_by_reference.outbound_channel: ', this.action.legal_entity_by_reference.outbound_channel);

			// Populate channel-specific form fields
			this.populateChannelFields();

			// Fetch recipient, contacts, then render template
			const recipientRef = this.action.recipient;
			this.$apollo
				.query({
					query: GET_RECIPIENT,
					variables: {
						recipient: recipientRef,
						participant: this.selectedParticipantStore
					}
				})
				.then(({ data }) => {
					this.currentRecipient = data.risolto_recipient[0];

					if (this.currentRecipient.custom_fields) {
						this.customFieldsJsonEditorData = this.currentRecipient.custom_fields;
					}

					this.$apollo
						.query({
							query: GET_CONTACTS,
							variables: {
								participant: this.currentRecipient.participant,
								recipient: this.currentRecipient.reference
							}
						})
						.then(({ data }) => {
							this.contacts = data.contacts;
							for (let contact of data.contacts) {
								let availableEndpoints = contact.channel_endpoints.filter(el => {
									if (el.channel === 'PHONE' || el.channel === 'EMAIL' || el.channel === 'MAIL') {
										if (el.channel === 'PHONE') el.icon = 'phone-solid';
										if (el.channel === 'EMAIL') el.icon = 'at-solid';
										if (el.channel === 'MAIL') el.icon = 'envelope-solid';
										if (el.channel === 'PEPPOL') el.icon = 'envelope-solid';
										return el;
									}
								});
								contact.channel_endpoints = availableEndpoints;
							}
						});

					if (this.currentRecipient.address) {
						if (objIsEmpty(this.currentRecipient.address)) {
							this.recipientAddressType = 'empty';
						} else {
							if (this.currentRecipient.address.hasOwnProperty('street_name')) {
								this.recipientAddressType = 'full';
							} else if (
								this.currentRecipient.address.hasOwnProperty('street_name_number_box')
							) {
								this.recipientAddressType = 'minimal';
							}
						}
					} else {
						Logging.log('address not available');
					}

					this.participantRef = this.currentRecipient.participant;

					this.resetAndRenderTemplate();
				})
				.catch(e => {
					console.error('Error in actionDetailLogic: ', e);
				});
		},
		/** Populate all channel-specific form fields (TO/FROM) from channels[0] metadata */
		populateChannelFields() {
			if (!this.action?.metadata?.channels?.[0]) return;

			const channelMetadata = this.action.metadata.channels[0];
			this.emailForm.subject = channelMetadata.subject || this.action.metadata.subject;

			// TO
			if (this.action.channel === 'SMS') {
				try {
					this.smsForm.to = channelMetadata.to || this.action.channel_endpoint_by_reference.endpoint;
				} catch (e) {
					this.smsForm.to = '';
				}
			} else if (this.action.channel === 'EMAIL') {
				this.emailForm.to = '';
				this.emailForm.cc = '';
				this.emailForm.bcc = '';
				if (this.action.metadata.channels.length > 0) {
					this.emailForm.to = channelMetadata.to;
					if (channelMetadata.cc) {
						this.emailForm.cc = channelMetadata.cc.join(', ');
					}
					if (channelMetadata.bcc) {
						this.emailForm.bcc = channelMetadata.bcc.join(', ');
					}
				}
				if ((!this.emailForm.to) && this.action?.channel_endpoint_by_reference?.endpoint) {
					this.emailForm.to = this.action.channel_endpoint_by_reference.endpoint;
					this.isDirty = true;
				}
			} else if (this.action.channel === 'MAIL') {
				this.populateMailToForm(channelMetadata);
			} else if (this.action.channel === 'PEPPOL') {
				this.fromPeppolForm.party_name = channelMetadata._from.party_name;
				this.fromPeppolForm.vat_number = channelMetadata._from.vat_number;
				this.fromPeppolForm.peppol_identifier = channelMetadata._from.peppol_identifier;
				this.toPeppolForm.party_name = channelMetadata.to.party_name;
				this.toPeppolForm.vat_number = channelMetadata.to.vat_number;
				this.toPeppolForm.peppol_identifier = channelMetadata.to.peppol_identifier;
				this.peppolDocForm.document_id = channelMetadata.document_id;
				this.peppolDocForm.document_type = channelMetadata.document_type;
				if (channelMetadata.S3Attachment) {
					this.peppolUblAttachment = channelMetadata.S3Attachment;
				}
			} else if (this.action.channel === 'NONE' && this.action.message_code === 'direct_debit') {
				this.directDebitForm.document_number = channelMetadata.document_number;
				this.directDebitForm.mandate_id = channelMetadata.mandate_id;
				this.directDebitForm.amount = channelMetadata.amount;
			}

			// FROM
			const outbound_channel = this.action.legal_entity_by_reference.outbound_channel;
			if (this.action.channel === 'EMAIL') {
				this.emailForm.from = channelMetadata.from || channelMetadata._from || outbound_channel.email_from;
			} else if (this.action.channel === 'MAIL') {
				this.populateMailFromForm(channelMetadata, outbound_channel);
			}

			// Trigger form validation
			this.$nextTick(() => {
				switch (this.componentType) {
					case 'EMAIL':
						this.$refs.emailForm?.validate(() => {});
						break;
					case 'MAIL':
						if (this.toAddressFull) {
							this.$refs.toMailFormFull?.validate(() => {});
						} else {
							this.$refs.toMailFormMinimal?.validate(() => {});
						}
						break;
					case 'SMS':
						this.$refs.smsForm?.validate(() => {});
						break;
				}
			});
		},
		/** Populate MAIL "to" address form fields from channel metadata */
		populateMailToForm(channelMetadata: Mail_Metadata) {
			this.toAddressFull = true;
			this.toMailFormFull.companyName = '';
			this.toMailFormFull.streetName = '';
			this.toMailFormFull.streetNumber = '';
			this.toMailFormFull.postBox = '';
			this.toMailFormFull.city = '';
			this.toMailFormFull.iso_country_code = '';
			try {
				const to = channelMetadata.to || this.action.channel_endpoint_by_reference.endpoint_details;
				const address = to.address || to;
				if (address && address.street_name_number_box) {
					this.toAddressFull = false;
					this.toMailFormMinimal.companyName = address.company_name || '';
					this.toMailFormMinimal.addressLine = address.street_name_number_box || 'N/A';
					this.toMailFormMinimal.city = address.city || '';
					this.toMailFormMinimal.iso_country_code = address.iso_country_code || '';
					this.toMailFormMinimal.zipCode = address.zip_code || address.postal_code || '';
				} else {
					this.toAddressFull = true;
					this.toMailFormFull.companyName = address.company_name || '';
					this.toMailFormFull.streetName = address.street_name || '';
					this.toMailFormFull.streetNumber = address.street_number || '';
					this.toMailFormFull.postBox = address.post_box || '';
					this.toMailFormFull.city = address.city || '';
					this.toMailFormFull.iso_country_code = address.iso_country_code || '';
					this.toMailFormFull.zipCode = address.zip_code || address.postal_code || '';
				}
			} catch (e) {
				try {
					Logging.log('metadata to', this.action.metadata.channels[0].to);
					Logging.log('legal entity to', this.action.channel_endpoint_by_reference.endpoint_details);
				} catch (e) {
					// ignore log failures
				}
			}
		},
		/** Populate MAIL "from" address form fields from channel metadata */
		populateMailFromForm(channelMetadata: Mail_Metadata, outbound_channel) {
			this.fromAddressFull = true;
			this.fromMailFormFull.streetName = '';
			this.fromMailFormFull.streetNumber = '';
			this.fromMailFormFull.postBox = '';
			this.fromMailFormFull.city = '';
			this.fromMailFormFull.iso_country_code = '';

			try {
				const from = channelMetadata.from || channelMetadata._from || outbound_channel.postal_address;
				const address = from.address || from;
				if (address && address.street_name_number_box) {
					this.fromAddressFull = false;
					this.fromMailFormMinimal.addressLine = address.street_name_number_box || 'N/A';
					this.fromMailFormMinimal.city = address.city || '';
					this.fromMailFormMinimal.iso_country_code = address.iso_country_code || '';
					this.fromMailFormMinimal.zipCode = address.zip_code || address.postal_code || '';
				} else {
					this.fromAddressFull = true;
					this.fromMailFormFull.streetName = address.street_name || '';
					this.fromMailFormFull.streetNumber = address.street_number || '';
					this.fromMailFormFull.postBox = address.post_box || '';
					this.fromMailFormFull.city = address.city || '';
					this.fromMailFormFull.iso_country_code = address.iso_country_code || '';
					this.fromMailFormFull.zipCode = address.zip_code || address.postal_code || '';
				}
			} catch (e) {
				// pass
			}
		},
		/** Reset render state and re-render template */
		resetAndRenderTemplate() {
			this.renderedHTMLBase64 = undefined;
			this.renderedHTMLUri = undefined;
			this.initialPlainHTML = undefined;
			this.tinymcecontent = null;
			this.showTinyMCEEditor = false;
			this.showRenderedBase64 = false;
			this.renderTemplate();
		},
		renderTemplate() {
			let payload;
			if (this.action) {
				// GET ALL ATTACHMENTS
				this.currentAttachments = [];
				if (this.action.metadata.channels[0].attachments) {
					for (let attachment of this.action.metadata.channels[0].attachments) {
						this.currentAttachments.push(attachment);
					}
				}
				if (this.action.metadata.attachments) {
					for (let attachment of this.action.metadata.attachments) {
						this.currentAttachments.push(attachment);
					}
				}
				// console.log('this.currentAttachments: ', this.currentAttachments);

				if ((this.action && this.action.channel !== 'PHONE') || (this.metadata && this.metadata.channel !== 'PHONE')) {
					if (this.action.metadata.channels[0].hasOwnProperty('template')) {
						let toneOfVoice = this.action.metadata.channels[0].template.tone_of_voice;
						let templateId = this.action.metadata.channels[0].template.template_id;
						let locale = this.action.metadata.channels[0].template.locale;
						let channel = this.action.metadata.channels[0].template.channel;

						if (!toneOfVoice || toneOfVoice === '') Logging.log("There's no tone of voice");
						if (!templateId || templateId === '') Logging.log("There's no template id");
						if (!channel || channel === '') Logging.log("There's no channel");
						if (!locale || locale === '') Logging.log("There's no locale on body template");

						payload = {
							channel: this.action.channel,
							tone_of_voice: toneOfVoice,
							locale: locale,
							template_id: templateId,
							participant: this.currentRecipient.participant,
							legal_entity: this.action.legal_entity,
							template_query_variables: {
								action: this.action.reference
							}
						};
					} else if (!this.action.metadata.channels[0].hasOwnProperty('template')) {
						Logging.log('no cover_letter available');
						this.hasCoverLetter = false;
					}
				}
			} else {
				payload = {
					channel: this.selectedTemplate.channel,
					legal_entity: this.selectedTemplate.legal_entity,
					tone_of_voice: this.selectedTemplate.tone_of_voice,
					locale: this.selectedTemplate.locale,
					template_id: this.selectedTemplate.template_id,
					participant: this.selectedTemplate.participant,
					sample_run: true,
					template_query_variables: {}
				};
			}
			// if there is no body property available on channel[0] or at the root of the metadata
			// we're going to call template renderer. if there is a body, we're going to render that instead.
			// there is a body because the user already made changes to the default rendered template or the action has been executed
			if (this.action) {
				if (!this.action.metadata.body && !this.action.metadata.channels[0].body && this.action.metadata.channels[0].template !== undefined) {
					if ((this.action && this.action.channel !== 'PHONE') || (this.metadata && this.metadata.channel !== 'PHONE')) {
						renderTemplate(payload).then(result => {
							this.renderedHTMLBase64 = result.data.base64;
							try {
								this.renderedHTMLUri = result.data.temporary_uri.uri;
							} catch (e1) {
								Logging.log(e1);
							}
							this.initialPlainHTML = Base64.decode(this.renderedHTMLBase64);
							this.tinymcecontent = Base64.decode(this.renderedHTMLBase64);
							this.showTinyMCEEditor = true;
							this.showRenderedBase64 = true;

							if (!this.action.metadata.channels[0].subject) {
								this.emailForm.subject = result.data.subject || this.action.metadata.channels[0].subject || '';
							}
						});
					}
				} else if (this.action.metadata.channels[0].body !== null) {
					this.tinymcecontent = Base64.decode(this.action.metadata.channels[0].body) || '';
					this.showTinyMCEEditor = true;
					this.showRenderedBase64 = true;
				} else if (this.action.metadata.body !== null) {
					// backward compatibility case for old actions
					this.tinymcecontent = Base64.decode(this.action.metadata.body) || '';
					this.showTinyMCEEditor = true;
					this.showRenderedBase64 = true;
				} else {
					this.showTinyMCEEditor = false;
					this.showRenderedBase64 = false;
				}
			}
		},
		async onExecute() {
			let channel = this.action.channel;
			switch (channel) {
				case 'EMAIL':
					this.$refs.emailForm.validate(async (valid) => {
						if (valid) {
							if (this.dunningNoticeFormState.isDirty || this.isDirty) {
								await this.saveChanges();
							}
							this.execute();
						} else {
							return false;
						}
					});
					break;
				case 'SMS':
					this.$refs.smsForm.validate(async (valid) => {
						if (valid) {
							if (this.dunningNoticeFormState.isDirty || this.isDirty) {
								await this.saveChanges();
							}
							this.execute();
						} else {
							return false;
						}
					});
					break;
				case 'MAIL':
					if (this.toAddressFull) {
						this.$refs.toMailFormFull.validate(async (valid) => {
							if (valid) {
								if (this.dunningNoticeFormState.isDirty || this.isDirty) {
									await this.saveChanges();
								}
								this.execute();
							} else {
								return false;
							}
						});
					} else {
						this.$refs.toMailFormMinimal.validate(async (valid) => {
							if (valid) {
								if (this.dunningNoticeFormState.isDirty || this.isDirty) {
									await this.saveChanges();
								}
								this.execute();
							} else {
								return false;
							}
						});
					}
					break;
				default:
					if (this.dunningNoticeFormState.isDirty || this.isDirty) {
						await this.saveChanges();
					}
					this.execute();
			}
		},
		execute() {
			this.isExecutingAction = true;
			Logging.log('in onExecute, this.action: ', this.action);

			let variables = { metadata: {}, action: this.action.reference, channel: this.action.channel };
			let shouldExecute = true;

			if (this.action.channel === 'MAIL') {
				let channelMetadata: Mail_Metadata = this.action.metadata.channels[0];

				if (channelMetadata.to.address && !Utils.isValidAddress(channelMetadata.to.address)) {
					this.notifyWarning(this.$t('ActionDetail.error_address_not_valid'));
					shouldExecute = false;
				}

				if (channelMetadata.template == null && this.currentAttachments.length === 0) {
					this.notifyWarning(this.$t('ActionDetail.error_attachment_missing'));
					shouldExecute = false;
				}
			}

			if (shouldExecute) {
				const payload = {
					version: 'v1',
					participant: this.action.participant,
					actions: [this.action.reference],
					dry_run: false,
					force_send: this.$store.getters['forceSendStore']
				};

				Logging.log('execute action: ', payload);

				axios
					.post(`https://${process.env.VUE_APP_API_ENDPOINT}/dunning/v1/action/confirm`, payload, {
						headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
					})
					.then(() => {
						this.$notify({
							title: this.$t('success'),
							message: this.$t('actionExecuted', 1),
							type: 'success'
						});

						// Update status in Vuex — filter-aware getter hides the row
						this.localAction.status = 'EXECUTED';
						this.onActionChanged(this.localAction);
						this.isExecutingAction = false;
					})
					.catch(e => {
						this.isExecutingAction = false;
						Logging.error('Err in executeAction: ', e);
						this.$notify({ title: this.$t('somethingWentWrong'), type: 'error' });
					});
			} else {
				Logging.log("won't execute");
			}
		},
		notifyWarning(errorMessage) {
			this.$notify({
				title: this.$t('warning'),
				message: errorMessage,
				type: 'warning'
			});
		},
		async onMarkAsDone() {
			this.ismarkingAsDone = true;
			Logging.log('in markAsDone(), this.action: ', this.action);

			const payload = {
				version: 'v1',
				participant: this.action.participant,
				actions: [this.action.reference],
				dry_run: false,
				force_send: this.$store.getters['forceSendStore'],
				skip_execution: true
			};

			try {
				await axios.post(`https://${process.env.VUE_APP_API_ENDPOINT}/dunning/v1/action/skip`, payload, {
					headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
				});

				this.$notify({
					title: this.$t('success'),
					message: this.$t('markedAsDone'),
					type: 'success'
				});

				// Update status in Vuex — filter-aware getter hides the row
				this.localAction.status = 'DONE';
				this.onActionChanged(this.localAction);
				this.ismarkingAsDone = false;
			} catch (e) {
				this.ismarkingAsDone = false;
				Logging.error(e);
			}
		},
		onCancel() {
			// @ts-ignore
			this.isCancelingAction = true;
			getUserInfo().then(({ data }) => {
				const loggedInUserInfo = data.sub;

				this.$apollo
					.query({
						query: GET_PERSON_BY_EXTERNAL_ID,
						variables: {
							externalId: loggedInUserInfo
						}
					})
					.then(({ data }) => {
						const payload = {
							version: 'v1',
							participant: this.action.participant,
							actions: [this.action.reference],
							reason: ''
						};

						axios
							.post(`https://${process.env.VUE_APP_API_ENDPOINT}/dunning/v1/action/cancel`, payload, {
								headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
							})
							.then(() => {
								this.$notify({
									title: this.$t('success'),
									message: this.$tc('actionCanceled', 1),
									type: 'success'
								});

								// Update status in Vuex — filter-aware getter hides the row
								this.localAction.status = 'CANCELED';
								this.onActionChanged(this.localAction);
								this.isCancelingAction = false;
							});
					})
					.catch(e => {
						this.isCancelingAction = false;
						Logging.error('Err: ', e);
						this.$notify({
							title: this.$t('somethingWentWrong'),
							message: this.$t('somethingWentWrong'),
							type: 'error'
						});
					});
			});
		},
		async forceOpenAction() {
			this.$confirm(this.$t('can_not_be_undone'), this.$t('warning'), {
				confirmButtonText: this.$t('Ok'),
				cancelButtonText: this.$t('cancel')
			}).then(async () => {
				try {
					const forceOpenResponse = await actionForceOpen(
						this.selectedParticipantStore,
						[this.action]
					);
					if (forceOpenResponse.data.actions.includes(this.action.reference)) {
						this.localAction.status = Risolto_Action_Status_Enum.Open;
						this.onActionChanged(this.localAction);

						this.$notify({
							title: this.$tc('actionOpened', 1),
							type: 'success'
						});
					} else {
						throw new Error('ActionNotOpened');
					}
				} catch (e) {
					this.$notify({
						title: this.$t('somethingWentWrong'),
						message: this.$t('somethingWentWrong'),
						type: 'error'
					});
				}
			});
		},
		async forceCancelAction() {
			this.$confirm(this.$t('can_not_be_undone'), this.$t('warning'), {
				confirmButtonText: this.$t('Ok'),
				cancelButtonText: this.$t('cancel')
			}).then(async () => {
				try {
					await actionForceCancel(
						this.selectedParticipantStore,
						[this.action]
					);

					this.localAction.status = Risolto_Action_Status_Enum.Canceled;
					this.onActionChanged(this.localAction);

					this.$notify({
						title: this.$tc('actionCanceled', 1),
						type: 'success'
					});
				} catch (e) {
					this.$notify({
						title: this.$t('somethingWentWrong'),
						message: this.$t('somethingWentWrong'),
						type: 'error'
					});
				}
			});
		},
		onViewRecipientInNewTab() {
			Utils.openInNewTab({
				name: 'recipient-detail',
				params: {
					id: this.currentRecipient.reference,
					participant: this.currentRecipient.participant
				}
			});
		},
		onViewRecipient() {
			EventBus.$emit('dialogClose', {});
			if (this.currentRecipient.reference) {
				this.$router.push({
					name: 'recipient-detail',
					params: {
						id: this.currentRecipient.reference,
						participant: this.currentRecipient.participant
					}
				});
				this.$store.commit('actions/SET_SELECTED_ACTION_REF', null);
				this.$store.commit('risolto/SET_COMPONENT_TYPE_STORE', undefined);
				this.$store.commit('risolto/SET_ACTION_DETAIL_DIALOG_VISIBLE', false);
				this.$store.commit('risolto/SET_WORKLIST_GROUPED_STATE', {});
				this.$store.commit('risolto/SET_ACTION_TABLE_VISIBLE', false);
			} else if (this.risolto_action.recipient) {
				this.$router.push({
					name: 'recipient-detail',
					params: {
						id: this.risolto_action.recipient,
						participant: this.risolto_action.participant
					}
				});
				this.$store.commit('actions/SET_SELECTED_ACTION_REF', null);
				this.$store.commit('risolto/SET_COMPONENT_TYPE_STORE', undefined);
				this.$store.commit('risolto/SET_ACTION_DETAIL_DIALOG_VISIBLE', false);
				this.$store.commit('risolto/SET_WORKLIST_GROUPED_STATE', {});
				this.$store.commit('risolto/SET_ACTION_TABLE_VISIBLE', false);
			} else {
				Logging.log('Something wrong w/ onViewRecipient');
			}
		},
		handleClose() {
			this.$confirm(this.$t('sureClose'), this.$t('warning'), {
				confirmButtonText: 'OK',
				cancelButtonText: this.$t('cancel'),
				type: 'warning'
			})
				.then(() => {
					this.renderedHTMLBase64 = undefined;
					this.renderedHTMLUri = undefined;
					this.initialPlainHTML = undefined;
					this.emailForm = {
						from: '',
						to: '',
						subject: ''
					};
					this.mailForm = {
						address: ''
					};
					this.smsForm = {
						to: ''
					};
					this.showRenderedBase64 = false;
					this.$emit('onDialogClose', {});
				})
				.catch(() => {
					// this.$emit('onCallDialogClose', {});
				});
		},
		async downloadAttachment(attachment: Attachment) {
			// console.log('attachment: ', attachment);
			attachmentDownload(this.selectedParticipantStore, attachment);
		},
		formatMailTo(to: Address & { address?: Address }) {
			const address = to.address ? to.address : to;
			const _this = this;

			if (address.street_name_number_box) {
				return `
					${address.street_name_number_box}, ${address.zip_code} ${address.city}, ${_this.$translate.iso_country_code(address.iso_country_code)}
				`;
			} else if (address) {
				return `
					${address.street_name} ${address.street_number}${address.post_box ? ' ' + address.post_box : ''}, ${address.zip_code} ${address.city}, ${_this.$translate.iso_country_code(address.iso_country_code)}
				`;
			}
		},
		handleOnDirty(a, b, c) {},
		handleOnChange(a, b, c) {},
		handleOnFocusIn(a, b, c) {},
		handleOnKeyDown(a, b, c) {
			Logging.log('handleOnKeyDown:', a, b, c);
			this.isDirty = true;
		},
		handleCheckAllInvoicesChange(value) {
			if (this.dunningNoticeFormState.showAllInvoices) {
				this.dunningNoticeForm.selectedInvoices = value ? this.recipientRecord.invoice_documents.map((invoice: any) => invoice.reference) : [];
				this.dunningNoticeForm.attachedInvoices = value ? this.dunningNoticeForm.attachedInvoices : [];
			} else {
				this.dunningNoticeForm.selectedInvoices = value ? this.recipientRecord.invoice_documents.filter((invoice: any) => invoice.open_amount > 0.00).map((invoice: any) => invoice.reference) : [];
				this.dunningNoticeForm.attachedInvoices = value ? this.dunningNoticeForm.attachedInvoices : [];
			}

			this.isDirty = true;
			this.dunningNoticeFormState.isDirty = true;
		},
		handleCheckedInvoicesChange(row) {
			this.handleCheckedDocumentChange(this.dunningNoticeForm.selectedInvoices, this.dunningNoticeForm.attachedInvoices, row);

			this.isDirty = true;
			this.dunningNoticeFormState.isDirty = true;
		},
		handleCheckAllCreditNotesChange(value) {
			if (this.dunningNoticeFormState.showAllCreditNotes) {
				this.dunningNoticeForm.selectedCreditNotes = value ? this.recipientRecord.credit_notes.map((creditNote: any) => creditNote.reference) : [];
				this.dunningNoticeForm.attachedCreditNotes = value ? this.dunningNoticeForm.attachedCreditNotes : [];
			} else {
				this.dunningNoticeForm.selectedCreditNotes = value ? this.recipientRecord.credit_notes.filter((creditNote: any) => creditNote.open_amount > 0.00).map((creditNote: any) => creditNote.reference) : [];
				this.dunningNoticeForm.attachedCreditNotes = value ? this.dunningNoticeForm.attachedCreditNotes : [];
			}

			this.isDirty = true;
			this.dunningNoticeFormState.isDirty = true;
		},
		handleCheckedCreditNotesChange(row) {
			this.handleCheckedDocumentChange(this.dunningNoticeForm.selectedCreditNotes, this.dunningNoticeForm.attachedCreditNotes, row);

			this.isDirty = true;
			this.dunningNoticeFormState.isDirty = true;
		},
		handleCheckAllDebitNotesChange(value) {
			if (this.dunningNoticeFormState.showAllDebitNotes) {
				this.dunningNoticeForm.selectedDebitNotes = value ? this.recipientRecord.debit_notes.map((debitNote: any) => debitNote.reference) : [];
			} else {
				this.dunningNoticeForm.selectedDebitNotes = value ? this.recipientRecord.debit_notes.filter((debitNote: any) => debitNote.open_amount > 0.00).map((debitNote: any) => debitNote.reference) : [];
			}

			this.isDirty = true;
			this.dunningNoticeFormState.isDirty = true;
		},
		handleCheckedDebitNotesChange(row) {
			this.handleCheckedDocumentChange(this.dunningNoticeForm.selectedDebitNotes, [], row);

			this.isDirty = true;
			this.dunningNoticeFormState.isDirty = true;
		},
		handleCheckAllPaymentsChange(value) {
			if (this.dunningNoticeFormState.showAllPayments) {
				this.dunningNoticeForm.selectedPayments = value ? this.recipientRecord.payment_documents.map((payment: any) => payment.reference) : [];
			} else {
				this.dunningNoticeForm.selectedPayments = value ? this.recipientRecord.payment_documents.filter((payment: any) => payment.open_amount > 0.00).map((payment: any) => payment.reference) : [];
			}

			this.isDirty = true;
			this.dunningNoticeFormState.isDirty = true;
		},
		handleCheckedPaymentsChange(row) {
			this.handleCheckedDocumentChange(this.dunningNoticeForm.selectedPayments, [], row);

			this.isDirty = true;
			this.dunningNoticeFormState.isDirty = true;
		},
		handleDunningNoticeFormChange() {
			this.isDirty = true;
			this.dunningNoticeFormState.isDirty = true;
		},
		handleAdvancedOptionsChange() {
			this.isDirty = true;
		},
		handleCostSettingsChange(override: boolean = false) {
			if (override) {
				if (this.dunningNoticeForm.enabledOverrideReminderCosts) this.dunningNoticeForm.overrideReminderCosts = 0;
				else this.dunningNoticeForm.overrideReminderCosts = null;
			}

			this.isDirty = true;
			this.dunningNoticeFormState.isDirty = true;
		},
		handleOverrideReminderCostsInput() {
			const value = this.dunningNoticeForm.overrideReminderCosts
				.toString()
				.replace(/[^\d.]/g, '') // remove non-numeric
				.replace(/(\..*)\./g, '$1'); // allow only one dot

			if (value !== this.dunningNoticeForm.overrideReminderCosts) {
				this.dunningNoticeForm.overrideReminderCosts = value;
				this.isDirty = true;
				this.dunningNoticeFormState.isDirty = true;
			}
		},
		handleAttachAllInvoicesChange(value) {
			this.dunningNoticeForm.attachedInvoices = value ? this.invoices.filter((invoice: any) => invoice.hasPdfAttachment && this.dunningNoticeForm.selectedInvoices.indexOf(invoice.reference) !== -1).map((invoice) => invoice.reference) : [];

			this.isDirty = true;
			this.dunningNoticeFormState.isDirty = true;
		},
		handleAttachedInvoicesChange(row) {
			this.handleAttachedDocumentsChange(this.dunningNoticeForm.selectedInvoices, this.dunningNoticeForm.attachedInvoices, row);

			this.isDirty = true;
			this.dunningNoticeFormState.isDirty = true;
		},
		handleAttachAllCreditNotesChange(value) {
			this.dunningNoticeForm.attachedCreditNotes = value ? this.creditNotes.filter((creditNote: any) => creditNote.hasPdfAttachment && this.dunningNoticeForm.selectedCreditNotes.indexOf(creditNote.reference) !== -1).map((creditNote) => creditNote.reference) : [];

			this.isDirty = true;
			this.dunningNoticeFormState.isDirty = true;
		},
		handleAttachedCreditNotesChange(row) {
			this.handleAttachedDocumentsChange(this.dunningNoticeForm.selectedCreditNotes, this.dunningNoticeForm.attachedCreditNotes, row);

			this.isDirty = true;
			this.dunningNoticeFormState.isDirty = true;
		},
		updateCheckAllInvoices() {
			const checkedCount = this.recipientRecord.invoice_documents.filter((invoice: any) => this.dunningNoticeForm.selectedInvoices.indexOf(invoice.reference) !== -1).length;

			if (this.dunningNoticeFormState.showAllInvoices) {
				this.checkAllInvoices = checkedCount > 0 && checkedCount === this.recipientRecord.invoice_documents.length;
				this.isIndeterminateInvoices = checkedCount > 0 && checkedCount !== this.recipientRecord.invoice_documents.length;
			} else {
				this.checkAllInvoices = checkedCount > 0 && checkedCount === this.recipientRecord.invoice_documents.filter((invoice: any) => invoice.open_amount > 0.00).length;
				this.isIndeterminateInvoices = checkedCount > 0 && checkedCount !== this.recipientRecord.invoice_documents.filter((invoice: any) => invoice.open_amount > 0.00).length;
			}
		},
		updateCheckAllCreditNotes() {
			const checkedCount = this.recipientRecord.credit_notes.filter((creditNote: any) => this.dunningNoticeForm.selectedCreditNotes.indexOf(creditNote.reference) !== -1).length;

			if (this.dunningNoticeFormState.showAllCreditNotes) {
				this.dunningNoticeFormState.checkAllCreditNotes = checkedCount > 0 && checkedCount === this.recipientRecord.credit_notes.length;
				this.dunningNoticeFormState.isIndeterminateCreditNotes = checkedCount > 0 && checkedCount !== this.recipientRecord.credit_notes.length;
			} else {
				this.dunningNoticeFormState.checkAllCreditNotes = checkedCount > 0 && checkedCount === this.recipientRecord.credit_notes.filter((creditNote: any) => creditNote.open_amount > 0.00).length;
				this.dunningNoticeFormState.isIndeterminateCreditNotes = checkedCount > 0 && checkedCount !== this.recipientRecord.credit_notes.filter((creditNote: any) => creditNote.open_amount > 0.00).length;
			}
		},
		updateCheckAllDebitNotes() {
			const checkedCount = this.recipientRecord.debit_notes.filter((debitNote: any) => this.dunningNoticeForm.selectedDebitNotes.indexOf(debitNote.reference) !== -1).length;

			if (this.dunningNoticeFormState.showAllDebitNotes) {
				this.dunningNoticeFormState.checkAllDebitNotes = checkedCount > 0 && checkedCount === this.recipientRecord.debit_notes.length;
				this.dunningNoticeFormState.isIndeterminateDebitNotes = checkedCount > 0 && checkedCount !== this.recipientRecord.debit_notes.length;
			} else {
				this.dunningNoticeFormState.checkAllDebitNotes = checkedCount > 0 && checkedCount === this.recipientRecord.debit_notes.filter((debitNote: any) => debitNote.open_amount > 0.00).length;
				this.dunningNoticeFormState.isIndeterminateDebitNotes = checkedCount > 0 && checkedCount !== this.recipientRecord.debit_notes.filter((debitNote: any) => debitNote.open_amount > 0.00).length;
			}
		},
		updateCheckAllPayments() {
			const checkedCount = this.recipientRecord.payment_documents.filter((payment: any) => this.dunningNoticeForm.selectedPayments.indexOf(payment.reference) !== -1).length;

			if (this.dunningNoticeFormState.showAllPayments) {
				this.dunningNoticeFormState.checkAllPayments = checkedCount > 0 && checkedCount === this.recipientRecord.payment_documents.length;
				this.dunningNoticeFormState.isIndeterminatePayments = checkedCount > 0 && checkedCount !== this.recipientRecord.payment_documents.length;
			} else {
				this.dunningNoticeFormState.checkAllPayments = checkedCount > 0 && checkedCount === this.recipientRecord.payment_documents.filter((payment: any) => payment.open_amount > 0.00).length;
				this.dunningNoticeFormState.isIndeterminatePayments = checkedCount > 0 && checkedCount !== this.recipientRecord.payment_documents.filter((payment: any) => payment.open_amount > 0.00).length;
			}
		},
		updateAttachAllInvoices() {
			const checkedCount = this.recipientRecord.invoice_documents.filter((invoice: any) => this.dunningNoticeForm.selectedInvoices.indexOf(invoice.reference) !== -1).length;
			const checkedAttachedCount = this.recipientRecord.invoice_documents.filter((invoice: any) => this.dunningNoticeForm.selectedInvoices.indexOf(invoice.reference) !== -1 && this.dunningNoticeForm.attachedInvoices.indexOf(invoice.reference) !== -1).length;
			const attachedCount = this.recipientRecord.invoice_documents.filter((invoice: any) => this.dunningNoticeForm.attachedInvoices.indexOf(invoice.reference) !== -1).length;
			this.dunningNoticeFormState.checkAllInvoicesAttachment = checkedCount > 0 && checkedCount === checkedAttachedCount;

			this.dunningNoticeFormState.isIndeterminateInvoicesAttachment = attachedCount > 0 && (attachedCount !== checkedAttachedCount || checkedCount !== attachedCount);
		},
		updateAttachAllCreditNotes() {
			const checkedCount = this.recipientRecord.credit_notes.filter((creditNote: any) => this.dunningNoticeForm.selectedCreditNotes.indexOf(creditNote.reference) !== -1).length;
			const checkedAttachedCount = this.recipientRecord.credit_notes.filter((creditNote: any) => this.dunningNoticeForm.selectedCreditNotes.indexOf(creditNote.reference) !== -1 && this.dunningNoticeForm.attachedCreditNotes.indexOf(creditNote.reference) !== -1).length;
			const attachedCount = this.recipientRecord.credit_notes.filter((creditNote: any) => this.dunningNoticeForm.attachedCreditNotes.indexOf(creditNote.reference) !== -1).length;
			this.dunningNoticeFormState.checkAllCreditNotesAttachment = checkedCount > 0 && checkedCount === checkedAttachedCount;

			this.dunningNoticeFormState.isIndeterminateCreditNotesAttachment = attachedCount > 0 && (attachedCount !== checkedAttachedCount || checkedCount !== attachedCount);
		},
		handleCheckedDocumentChange(documents: string[], attachedDocuments: string[], row: {reference: string}) {
			const index = documents.indexOf(row.reference);
			const included = index !== -1;

			if (included) {
				documents.splice(index, 1);
				// Also remove the PDF attachment (if applicable)
				this.removeDocumentAttachment(attachedDocuments, row);
			} else {
				documents.push(row.reference);
			}
		},
		handleAttachedDocumentsChange(documents: string[], attachedDocuments: string[], row: {reference: string}) {
			const index = attachedDocuments.indexOf(row.reference);
			const included = index !== -1;

			if (included) {
				attachedDocuments.splice(index, 1);
			} else {
				attachedDocuments.push(row.reference);
				// Also add the corresponding credit note to the account overview
				this.addDocument(documents, row);
			}
		},
		setLoading(loading: boolean) {
			this.isUploadingAttachment = loading;
		},
		removeDocumentAttachment(attachedDocuments: string[], row: {reference: string}) {
			const index = attachedDocuments.indexOf(row.reference);
			const included = index !== -1;

			if (included) {
				attachedDocuments.splice(index, 1);
			} else {
				// Do nothing, already absent
			}
		},
		addDocument(documents: string[], row: {reference: string}) {
			const index = documents.indexOf(row.reference);
			const included = index !== -1;

			if (included) {
				// Do nothing, already present
			} else {
				documents.push(row.reference);
			}
		},
		toggleRightPanel(value) {
			this.$store.commit('actions/SET_ACTION_DETAIL_RIGHT_PANEL_COLLAPSED', value);
		},
		evaluateLayout() {
			if (this.actionDetailRightPanelCollapsed) {
				this.mainPanelWidth = 24;
				this.rightPanelWidth = 0;
			} else {
				this.mainPanelWidth = 17;
				this.rightPanelWidth = 7;
			}
		},
		isInPaymentPlan(invoice_document) {
			let isInPaymentPlan = false;
			this.recipientRecord.payment_plans.forEach(payment_plan => {
				payment_plan.invoice_group_by_reference.invoice_group_invoices.forEach(invoice_group_invoice => {
					if (invoice_group_invoice.invoice === invoice_document.reference) {
						isInPaymentPlan = true;
					}
				});
				payment_plan.invoice_group_by_reference.invoice_group_credit_notes.forEach(invoice_group_credit_note => {
					if (invoice_group_credit_note.credit_note === invoice_document.reference) {
						isInPaymentPlan = true;
					}
				});
				payment_plan.invoice_group_by_reference.invoice_group_debit_notes.forEach(invoice_group_debit_note => {
					if (invoice_group_debit_note.debit_note === invoice_document.reference) {
						isInPaymentPlan = true;
					}
				});
				payment_plan.invoice_group_by_reference.invoice_group_payments.forEach(invoice_group_payment => {
					if (invoice_group_payment.payment === invoice_document.reference) {
						isInPaymentPlan = true;
					}
				});
			});

			return isInPaymentPlan;
		},
		handleRegisteredChange() {
			if (!this.canEnableRegistered) {
				return;
			}

			const channel = this.action?.metadata?.channels?.[0];
			if (!channel) {
				return;
			}

			if (this.action.metadata.channels[0].registered) {
				this.action.metadata.channels[0].registered = false;
				this.isDirty = true;
				return;
			}

			this.$confirm(this.$t('letterItems.registeredMailWarning'), this.$t('warning'), {
				confirmButtonText: this.$t('Ok'),
				cancelButtonText: this.$t('cancel'),
				type: 'warning'
			}).then(() => {
				this.action.metadata.channels[0].registered = true;
				this.isDirty = true;
			});
		},
		downloadPeppolUbl(attachment: Attachment) {
			attachmentDownload(this.selectedParticipantStore, attachment);
		},
		beforePeppolUpload(file) {
			const isXML = file.type === 'text/xml' || file.type === 'application/xml' || file.name.endsWith('.xml');
			if (!isXML) {
				this.$message.error(this.$t('ActionDetail.peppol.onlyXmlAllowed'));
				return false;
			}
			this.isUploadingPeppol = true;
			return true;
		},
		handlePeppolUploadSuccess(response, file) {
			this.isUploadingPeppol = false;
			this.$message.success(this.$t('ActionDetail.peppol.uploadSuccess'));
			// Refresh action data
			this.$emit('refresh');
		},
		handlePeppolUploadError(err, file) {
			console.error('Peppol upload error:', err);
			this.isUploadingPeppol = false;
			this.$message.error(this.$t('ActionDetail.peppol.uploadError'));
		},
		async validatePeppolDocument() {
			this.isValidatingPeppol = true;
			try {
				const response = await axios.post(
					`https://${process.env.VUE_APP_API_ENDPOINT}/operator-fe/action/peppol/validate`,
					{
						participant: this.selectedParticipantStore,
						action: this.action.reference
					},
					{
						headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
					}
				);
				this.$message.success(this.$t('ActionDetail.peppol.validateSuccess'));
			} catch (error) {
				this.$message.error(this.$t('ActionDetail.peppol.validateError'));
			} finally {
				this.isValidatingPeppol = false;
			}
		},
		async resendViaPeppol() {
			this.$confirm(this.$t('ActionDetail.peppol.resendConfirm'), this.$t('warning'), {
				confirmButtonText: this.$t('Ok'),
				cancelButtonText: this.$t('cancel'),
				type: 'warning'
			}).then(async () => {
				this.isResendingPeppol = true;
				try {
					const response = await axios.post(
						`https://${process.env.VUE_APP_API_ENDPOINT}/operator-fe/action/peppol/resend`,
						{
							participant: this.selectedParticipantStore,
							action: this.action.reference
						},
						{
							headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
						}
					);
					this.$message.success(this.$t('ActionDetail.peppol.resendSuccess'));
					// Refresh action data
					this.$emit('refresh');
				} catch (error) {
					this.$message.error(this.$t('ActionDetail.peppol.resendError'));
				} finally {
					this.isResendingPeppol = false;
				}
			});
		}
	}
};
